import { useMutation, useQuery } from '@apollo/client';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { Icon, IconDelete } from '@flixbus/honeycomb-icons-react';
import {
	Button,
	DataTable,
	DataTableHead,
	DataTableRow,
	DataTableSorter,
	LegacyGrid as Grid,
	LegacyGridCol as GridCol,
} from '@flixbus/honeycomb-react';

import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { DELETE_CREW_MEMBER_ABSENCE } from 'common/gql/deleteAbsence';
import Loader from 'common/Loader';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { standardDeletedSuccess, standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import personalTimelineStore from 'pages/CrewMemberDetails/components/PersonalTimeline/PersonalTimelineStore';
import { crewMemberDetailsStore } from 'pages/CrewMemberDetails/crewMemberDetailsStore';
import { formatWeekdayDate } from 'utils/datetime';

import { applyFilters, sortedByRecently } from './filterAbsences';
import { headersTable } from './options';
import { ALL_REASONS_KEY, UNAVAILABILITY_TEXT } from './text';
import UnavailabilityFilter from './UnavailabilityFilter/UnavailabilityFilter';
import { FETCH_UNAVAILABITY_DAYS } from './unavailalbityQuery.gql';

import styles from './unavailability.module.css';

const Unavailability = observer(() => {
	const active = useActiveElements();
	const { employeeId } = crewMemberDetailsStore;
	const timezone = personalTimelineStore.homebaseTimezone?.value;
	const [crewMemberAbsences, setCrewMemberAbsences] = useState([]);
	const [reasons, setReasons] = useState([]);
	const [filters, setFilters] = useState({
		reason: ALL_REASONS_KEY,
		dateRange: '',
	});
	const [filteredItems, setFilteredItems] = useState([]);

	const { loading, error, data } = useQuery(FETCH_UNAVAILABITY_DAYS, {
		variables: {
			crewMemberId: employeeId,
		},
		// Belt-and-braces: the parent page already gates rendering on
		// `!employeeId`, but this skip documents the contract at the consumer
		// and protects against future regressions if a caller renders this
		// component before the store is seeded.
		skip: !employeeId,
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	const [deleteAbsence] = useMutation(DELETE_CREW_MEMBER_ABSENCE, {
		refetchQueries: [FETCH_UNAVAILABITY_DAYS],
		onCompleted: (data) => {
			if (data?.deleteCrewMemberAbsence.__typename === 'CrewMemberAbsenceErrorResponse')
				return notificationStore.setNotification({
					header: UNAVAILABILITY_TEXT.deleteAlreadyProcessedErrorHeader,
					label: UNAVAILABILITY_TEXT.deleteAlreadyProcessedErrorLabel,
					appearance: 'danger',
				});
			notificationStore.setNotification(standardDeletedSuccess);
			personalTimelineStore.fetchActivities();
		},
		onError: () => {
			notificationStore.setNotification(standardError);
		},
	});

	const handleDeleteAbsence = (absenceId) => {
		deleteAbsence({ variables: { id: absenceId } });
	};

	const onSortingChange = (currentSorting, keyName) => {
		let sorted = [...filteredItems];
		sorted =
			currentSorting !== 'ascending'
				? sorted.sort((a, b) => moment(a[keyName]).diff(moment(b[keyName])))
				: sorted.sort((a, b) => moment(b[keyName]).diff(moment(a[keyName])));
		setFilteredItems(sorted);
	};

	useEffect(() => {
		setCrewMemberAbsences(sortedByRecently(data?.crewMemberAbsences));
		setReasons(data?.crewMemberAbsenceReasons);
	}, [data]);

	useEffect(() => {
		setFilteredItems(applyFilters({ absences: crewMemberAbsences, filters }) ?? []);
	}, [filters, crewMemberAbsences]);

	if (error) return <div>{UNAVAILABILITY_TEXT.errorFallback}</div>;

	return (
		<section className={styles.unavailibity_wrapper}>
			<Grid>
				<GridCol size={8}>
					<UnavailabilityFilter
						onFiltersSubmited={setFilters}
						reasons={reasons}
						loading={loading}
					/>
				</GridCol>
				<GridCol size={4} extraClasses={styles.gridItem_right}>
					<Button
						appearance="primary"
						disabled={loading}
						onClick={() => active.setActiveElement(MODAL_NAMES.absence, { readOnly: false })}
					>
						<strong>{UNAVAILABILITY_TEXT.scheduleAbsenceButton}</strong>
					</Button>
				</GridCol>
			</Grid>
			{loading ? (
				<Loader />
			) : (
				<DataTable
					extraClasses={styles.dataTable_extraClasses}
					headers={
						<DataTableHead>
							{headersTable.map((thTitle) =>
								thTitle.sort ? (
									<DataTableSorter
										key={thTitle.name}
										onSortingChange={(currenSorting) =>
											onSortingChange(currenSorting, thTitle.keyName)
										}
									>
										{thTitle.name}
									</DataTableSorter>
								) : (
									<th key={thTitle.name} colSpan={thTitle.size}>
										{thTitle.name}
									</th>
								)
							)}
						</DataTableHead>
					}
				>
					{filteredItems !== undefined && filteredItems.length > 0 ? (
						filteredItems.map((absence) => (
							<DataTableRow key={absence.id}>
								<td>{formatWeekdayDate(absence.fromTimestamp, timezone)}</td>
								<td>{formatWeekdayDate(absence.toTimestamp, timezone)}</td>
								<td>
									{absence.reason.isPayable ? `$ ${absence.reason.name}` : absence.reason.name}
								</td>
								<td>{formatWeekdayDate(absence.createdAt)}</td>
								<td>{absence.updatedBy}</td>
								<td>
									<div className={styles.booked_wrapper}>
										<span>{absence.note}</span>
										<span className={styles.triggerDelete}>
											<Icon
												InlineIcon={IconDelete}
												title={UNAVAILABILITY_TEXT.deleteIconTitle}
												onClick={() => handleDeleteAbsence(absence.id)}
											/>
										</span>
									</div>
								</td>
							</DataTableRow>
						))
					) : (
						<DataTableRow key="empty">
							<td colSpan={5} className={styles.noActions} align="center">
								{UNAVAILABILITY_TEXT.noResults}
							</td>
						</DataTableRow>
					)}
				</DataTable>
			)}
		</section>
	);
});

export default Unavailability;
